
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { checkAndSyncEmail } from '@/utils/emailSync';

interface AuthCheckResult {
  user: User | null;
  isProfileComplete: boolean;
  isLoading: boolean;
}

export const useAuthCheck = (): AuthCheckResult => {
  const [user, setUser] = useState<User | null>(null);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthAndProfile = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          setUser(session.user);
          
          // Check if profile is complete and sync email if needed
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, phone_number, email')
            .eq('id', session.user.id)
            .maybeSingle();

          if (profile) {
            // Sync email if needed
            await checkAndSyncEmail(session.user.id, session.user.email);
            
            // Force another sync after a delay to ensure consistency
            setTimeout(() => {
              checkAndSyncEmail(session.user.id, session.user.email);
            }, 2000);

            setIsProfileComplete(!!(profile.full_name && profile.phone_number));
          } else {
            // Profile doesn't exist, it's incomplete
            setIsProfileComplete(false);
          }
        } else {
          setUser(null);
          setIsProfileComplete(false);
        }
      } catch (error) {
        console.error('Error checking auth and profile:', error);
        setUser(null);
        setIsProfileComplete(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndProfile();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        if (session?.user) {
          setUser(session.user);
          
          // Check profile completion when user signs in or updates
          setTimeout(async () => {
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('full_name, phone_number, email')
                .eq('id', session.user.id)
                .maybeSingle();

              if (profile) {
                // Sync email if needed
                await checkAndSyncEmail(session.user.id, session.user.email);
                
                // Force another sync after a delay to ensure consistency
                setTimeout(() => {
                  checkAndSyncEmail(session.user.id, session.user.email);
                }, 2000);

                setIsProfileComplete(!!(profile.full_name && profile.phone_number));
              } else {
                setIsProfileComplete(false);
              }
            } catch (error) {
              console.error('Error checking profile completion:', error);
              setIsProfileComplete(false);
            }
          }, 100);
        } else {
          setUser(null);
          setIsProfileComplete(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return { user, isProfileComplete, isLoading };
};
